import { yaml } from '@codemirror/lang-yaml';
import { type Diagnostic, linter } from '@codemirror/lint';
import type { Extension } from '@codemirror/state';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import React, { useMemo } from 'react';

import { YamlEditorWrapper } from './YamlEditor.styles';

export interface YamlEditorDiagnostic {
  from: number;
  to: number;
  severity: 'error' | 'warning' | 'info';
  message: string;
  source?: string;
}

export interface YamlEditorProps {
  value: string;
  onChange?: (value: string) => void;
  height?: string;
  readOnly?: boolean;
  placeholder?: string;
  theme?: 'light' | 'dark';
  diagnostics?: YamlEditorDiagnostic[];
  lineWrapping?: boolean;
}

export const YamlEditor: React.FC<YamlEditorProps> = ({
  value,
  onChange,
  height = '100%',
  readOnly = false,
  placeholder = 'Enter YAML configuration...',
  theme = 'light',
  diagnostics = [],
  lineWrapping = false,
}) => {
  // Create a linter extension that uses the provided diagnostics
  const diagnosticLinter = useMemo(() => {
    return linter(() => {
      return diagnostics.map(
        (diagnostic): Diagnostic => ({
          from: diagnostic.from,
          to: diagnostic.to,
          severity: diagnostic.severity,
          message: diagnostic.message,
          source: diagnostic.source,
        }),
      );
    });
  }, [diagnostics]);

  // Build extensions array
  const extensions = useMemo(() => {
    const baseExtensions: Extension[] = [yaml()];

    // Always add linter (it will just return empty diagnostics if none provided)
    baseExtensions.push(diagnosticLinter);

    // Add line wrapping if enabled
    if (lineWrapping) {
      baseExtensions.push(EditorView.lineWrapping);
    }

    return baseExtensions;
  }, [diagnosticLinter, lineWrapping]);

  return (
    <YamlEditorWrapper>
      <CodeMirror
        value={value}
        onChange={onChange}
        extensions={extensions}
        theme={theme === 'dark' ? oneDark : undefined}
        editable={!readOnly}
        placeholder={placeholder}
        height={height}
        basicSetup={{
          drawSelection: true,
          lineNumbers: true,
          foldGutter: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          searchKeymap: true,
          highlightSelectionMatches: true,
        }}
      />
    </YamlEditorWrapper>
  );
};

export default YamlEditor;
